import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiTableComponent,
  type HeaderConfig,
  TableRow,
  QaiLoadingComponent,
  QaiFieldComponent,
  QaiInputComponent,
  QaiIconComponent,
  QaiSearchableSelectComponent,
  QaiSearchableSelectOption,
  QaiSlotDirective
} from '@qai/ui';
import { IUserCertificationDetail } from 'src/app/interfaces/reportingV2/icertification-users';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { getCertificationUsersTableHeaders } from 'src/app/utils/configs/reporting/table-configs';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { CsvExportService, csvValue } from 'src/app/services/general/csv-export.service';
import { LocaleService } from 'src/app/services/locale.service';
import { Router } from '@angular/router';
import { localeLowerCase } from '../../../../../../../utils/locale-case.util';

@Component({
  selector: 'app-certification-users',
  templateUrl: './certification-users.component.html',
  styleUrls: ['./certification-users.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    QaiTableComponent,
    QaiLoadingComponent,
    CommonModule,
    QaiFieldComponent,
    QaiInputComponent,
    TranslocoModule,
    FormsModule,
    QaiIconComponent,
    QaiSearchableSelectComponent,
    QaiSlotDirective,
  ],
})
export class CertificationUsersComponent implements OnInit, OnChanges {
  tableData: TableRow[] = [];
  filteredData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  searchTerm: string = '';
  certificationStatuses: QaiSearchableSelectOption[] = [];
  selectedStatuses: string[] | string = [];
  totalUsers = 16;
  isLoading = false;
  isTooltipLoading = false;
  isTranslationLoaded: boolean = false;
  currentPage: number = 1;
  @ViewChild(QaiTableComponent) tableRef!: QaiTableComponent;
  @Input() pageSize = 5;
  @Input() isUserModalOpen = false;
  @Output() expandUserModal = new EventEmitter<void>();
  @Output() closeUserModal = new EventEmitter<void>();
  @Input() certificationStatus: string | null = null;
  @Input() moduleAssignmentUuid: string = '';
  @Input() teamUuids: string[] = [];
  @Input() userData: IUserCertificationDetail[] = [];
  @Input() isLoadingData: boolean = false;
  @Input() assignmentName: string = '';
  scopedOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('OTHERS_SINGULAR', {}, 'reportingV2')
      : this.translocoService.translate('OTHERS_PLURAL', { count }, 'reportingV2');

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private amplitudeService: AmplitudeService,
    private csvExportService: CsvExportService,
    private localeService: LocaleService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.scopedOptionsSelectionMode.selectAllOption.label = this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionMode.deselectAllOption.label = this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
      this.loadCertificationStatuses();
      this.initializeTable();
      this.loadCertificationUsers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userData'] || changes['isLoadingData']) {
      this.loadCertificationUsers();
      if (this.certificationStatus) {
        const mappedStatus = this.mapRouteStatusToComponentStatus(
          this.certificationStatus
        );
        this.onStatusValueChange(mappedStatus);
      }
    }
  }

  loadCertificationStatuses(): void {
    this.certificationStatuses = [
      {
        label: this.translocoService.translate('reportingV2.ASSIGNED_LABEL'),
        value: this.translocoService.translate('reportingV2.ASSIGNED_LABEL'),
      },
      {
        label: this.translocoService.translate('reportingV2.IN_PROGRESS_LABEL'),
        value: this.translocoService.translate('reportingV2.IN_PROGRESS_LABEL'),
      },
      {
        label: this.translocoService.translate('reportingV2.CERTIFIED_LABEL'),
        value: this.translocoService.translate('reportingV2.CERTIFIED_LABEL'),
      },
      {
        label: this.translocoService.translate(
          'reportingV2.OUT_OF_ATTEMPTS_LABEL'
        ),
        value: this.translocoService.translate(
          'reportingV2.OUT_OF_ATTEMPTS_LABEL'
        ),
      },
    ];
  }

  /**
   * Maps route parameter status values to component expected status values
   * Uses translation service to handle all supported languages dynamically
   * Route: "In progress" -> Component: "In Progress"
   * Route: "Out of attempts" -> Component: "Out of Attempts"
   */
  private mapRouteStatusToComponentStatus(routeStatus: string): string {
    // Get translated values with fallbacks
    const inProgressLabel =
      this.translocoService.translate('reportingV2.IN_PROGRESS_LABEL') ||
      'In progress';
    const outOfAttemptsLabel =
      this.translocoService.translate('reportingV2.OUT_OF_ATTEMPTS_LABEL') ||
      'Out of attempts';

    const statusMapping: { [key: string]: string } = {
      [inProgressLabel]: inProgressLabel,
      [outOfAttemptsLabel]: outOfAttemptsLabel,
    };

    return statusMapping[routeStatus] || routeStatus;
  }

  pageChanged(event: { page: number }): void {
    this.currentPage = event.page;
  }

  onSortChanged(event: any) {
    console.log('Sort changed:', event);
  }

  getEmptyStateMessage(): string {
    if (this.tableData.length === 0) {
      return this.translocoService.translate(
        'reportingV2.NO_USERS_FOUND_LABEL'
      );
    }
    if (this.filteredData.length === 0) {
      return this.translocoService.translate(
        'reportingV2.NO_USERS_MATCHING_FILTERS_LABEL'
      );
    }
    return '';
  }

  protected onDownloadIconClick(): void {
    const fileName = this.csvExportService.getCsvFileName(
      this.assignmentName,
      'user_data'
    );
    const csvData = this.tableData.map((row) => {
      const csvRow: Record<string, unknown> = {};
      this.headers.forEach((header) => {
        const cell = row[header.name];
        if (cell?.type === 'tooltip' && Array.isArray(cell.value)) {
          csvRow[header.name] = cell.value.join(', ');
        } else if (cell?.type === 'text') {
          csvRow[header.name] = (cell as { csvValue?: unknown }).csvValue ?? cell.value;
        } else {
          csvRow[header.name] = cell;
        }
      });
      return csvRow;
    });
    const headerNames = this.headers.map((header) => header.name);
    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }

  toggleModal() {
    this.clearFiltersAndSearch();
    if (this.isUserModalOpen) {
      this.closeUserModal.emit();
    } else {
      this.expandUserModal.emit();
    }
  }

  private clearFiltersAndSearch() {
    this.searchTerm = '';
    this.selectedStatuses = [];
    this.filteredData = [...this.tableData];
  }

  private initializeTable() {
    this.headers = getCertificationUsersTableHeaders(this.translocoService);
  }

  public loadCertificationUsers(): void {
    this.isLoading = this.isLoadingData;

    if (this.userData && this.userData.length > 0) {
      this.tableData = this.transformApiDataToTableRows(this.userData);
      this.totalUsers = this.userData.length;
      this.currentPage = 1;
      this.filteredData = this.tableData;
      this.applyStatusFilter();
    } else {
      this.tableData = [];
      this.totalUsers = 0;
      this.filteredData = [];
    }
  }

  private transformApiDataToTableRows(
    apiData: IUserCertificationDetail[]
  ): TableRow[] {
    return apiData.map((user) => this.createUserTableRow(user));
  }

  private createUserTableRow(user: IUserCertificationDetail): TableRow {
    const row = {
      id: user.id,
      [this.headers[0].name]: { type: 'text', value: user.name },
      [this.headers[1].name]: { type: 'text', value: user.email },
      [this.headers[2].name]:
        user.teams.length > 1
          ? {
              type: 'tooltip',
              value: user.teams,
              label: `${user.teams.length} teams`,
            }
          : user.teams.length === 1
          ? { type: 'text', value: user.teams[0] }
          : { type: 'text', value: '' },
      [this.headers[3].name]:
        user.certificationStatus ===
        this.translocoService.translate('reportingV2.OUT_OF_ATTEMPTS_LABEL')
          ? { type: 'text', value: user.certificationStatus, kind: 'error' }
          : { type: 'text', value: user.certificationStatus },
      [this.headers[4].name]: {
        type: 'text',
        value: this.translocoService.translate(
          user.certifiedOnFirstAttempt ? 'reportingV2.YES_LABEL' : 'reportingV2.NO_LABEL'
        ),
      },
      [this.headers[5].name]: {
        type: 'text',
        value:
          this.localeService.formatTableDate(user.dateOfCertification)?.toString() ||
          user.dateOfCertification,
        ...csvValue(this.localeService.formatCsvDate(user.dateOfCertification)),
      },
      [this.headers[6].name]: {
        type: 'text',
        value: this.localeService.formatScoreValue(user.highScore),
      },
    } as const;

    return { ...row } as unknown as TableRow;
  }

  onTooltipDataRequested = ({ row }: { row: TableRow }) => {
    this.isTooltipLoading = true;

    const cell = row[this.headers[2].name];

    if (cell.type !== 'tooltip') {
      this.isTooltipLoading = false;
      return;
    }

    setTimeout(() => {
      this.tableRef?.setTooltipData(cell.value);
      this.isTooltipLoading = false;
    }, 300);
  };

  private extractCellValue(cell: any): string {
    return cell && cell.type === 'text' && cell.value ? cell.value : '';
  }

  protected onSearchChange(event: Event) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.searchTerm = value;

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_USER_TABLE_SEARCH
    );

    this.onSearch();
  }

  onSearch() {
    const activeLang = this.translocoService.getActiveLang() || 'en';
    // REMEDIATE: user-typed search input
    const normalizedTerm = localeLowerCase(this.searchTerm, activeLang).trim();

    if (!normalizedTerm) {
      this.applyStatusFilter();
      return;
    }

    const baseData = this.selectedStatuses.length
      ? this.filteredData
      : this.tableData;

    this.filteredData = !normalizedTerm
      ? baseData
      : baseData.filter((row) => {
          // REMEDIATE: learner display name matched against search
          const nameCell = localeLowerCase(
            this.extractCellValue(row[this.headers[0].name]),
            activeLang
          );
          // REMEDIATE: email matched against search
          const emailCell = localeLowerCase(
            this.extractCellValue(row[this.headers[1].name]),
            activeLang
          );
          const teamsCell = row[this.headers[2].name];
          let teams = '';

          if (teamsCell?.type === 'tooltip') {
            // REMEDIATE: team names matched against search
            teams = localeLowerCase((teamsCell.value as string[]).join(' '), activeLang);
          } else if (teamsCell?.type === 'text') {
            // REMEDIATE: team name matched against search
            teams = localeLowerCase(String(teamsCell.value), activeLang);
          }

          return (
            nameCell.includes(normalizedTerm) ||
            emailCell.includes(normalizedTerm) ||
            teams.includes(normalizedTerm)
          );
        });

    this.currentPage = 1;
  }

  onStatusValueChange(selectedValue: string | string[]) {
    if (
      selectedValue ===
      this.translocoService.translate('USERS_ASSIGNED_LABEL', {}, 'reportingV2')
    ) {
      return;
    }

    this.selectedStatuses = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];

    this.applyStatusFilter();
  }

  onCertificationStatusSelected(statusLabel: string) {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_ENGAGEMENT_STATUS_FILTER,
      { engagement_status: statusLabel }
    );
  }

  applyStatusFilter() {
    if (this.selectedStatuses.length === 0) {
      if (this.searchTerm) {
        this.onSearch();
      } else {
        this.filteredData = this.tableData;
      }
    } else {
      const sourceData = this.searchTerm ? this.filteredData : this.tableData;

      this.filteredData = sourceData.filter((row) => {
        const statusHeaderName = this.headers[3]?.name;
        if (!statusHeaderName) {
          return false;
        }

        const statusCell = row[statusHeaderName] as {
          type: 'text';
          value: string;
        };

        if (!statusCell) {
          return false;
        }

        const status = statusCell.value;
        return this.selectedStatuses.includes(status);
      });
    }
    this.currentPage = 1;
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }) {
    this.navigateToUserDetail(event.rowId);
  }

  onCellClick(event: { rowData: TableRow; columnName: string; event: Event }) {
    this.navigateToUserDetail(event.rowData.id);
  }

  navigateToUserDetail(userId: string | number) {
    this.router.navigate(['/reporting_dashboard/user', userId], {
      queryParams: { assignmentUuid: this.moduleAssignmentUuid },
    });
  }
}
